import { useState, useEffect, useMemo } from "react";
import { appointmentsApi } from "@services/api/appointments";
import { useDataStore } from "@store/user_org";
import { navigateTo } from "context/navigation.service";
import type { Appointment } from "types/garage/planning";
import { getCategories } from "@store/planningStore";
import LoadingBar from "@components/ui/LoadingBar";

const FALLBACK_COLORS = { bg: "#F3F4F6", text: "#374151" };

function formatHour(h: number): string {
  const hours = Math.floor(h);
  const mins = Math.round((h - hours) * 60);
  return `${hours}h${mins > 0 ? String(mins).padStart(2, "0") : "00"}`;
}

export default function RdvDuJourWidget() {
  const orgData = useDataStore((s) => s.orgData);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);

  const categories = useMemo(() => getCategories(orgData), [orgData]);
  const catColors = useMemo(() => {
    const m: Record<string, { bg: string; text: string }> = {};
    for (const c of categories) m[c.id] = { bg: c.bgColor, text: c.color };
    return m;
  }, [categories]);
  const catLabels = useMemo(() => {
    const m: Record<string, string> = {};
    for (const c of categories) m[c.id] = c.label;
    return m;
  }, [categories]);

  useEffect(() => {
    if (!orgData?.id) return;
    const today = new Date().toISOString().split("T")[0];
    setLoading(true);
    appointmentsApi
      .list({ org_id: orgData.id, date: today })
      .then((data) => {
        setAppointments(data.sort((a, b) => a.startHour - b.startHour));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [orgData?.id]);

  return (
    <div className="flex flex-col bg-white border rounded-xl p-4 gap-2 shadow-lg relative overflow-hidden">
      {loading && <LoadingBar variant="absolute" />}
      <div className="flex flex-row justify-between items-center mb-1">
        <div className="font-semibold text-black">Rendez-vous du jour</div>
        <button
          onClick={() => navigateTo({ route: "/reception" })}
          className="text-h3 text-neutral-400 hover:text-neutral-600"
        >
          Voir planning
        </button>
      </div>
      {!loading && appointments.length === 0 && (
        <div className="text-h3 text-neutral-400 py-2">Aucun rendez-vous aujourd'hui</div>
      )}
      <div className="flex flex-col divide-y">
        {appointments.slice(0, 8).map((rdv) => {
          const colors = catColors[rdv.category] || FALLBACK_COLORS;
          return (
            <div
              key={rdv.id}
              className="flex flex-row items-center py-2.5 px-1 gap-3 cursor-pointer hover:bg-neutral-50 rounded-lg"
              onClick={() => rdv.ordre_id ? navigateTo({ route: `/ordre/${rdv.ordre_id}` }) : navigateTo({ route: "/reception" })}
            >
              <div className="text-h2 font-medium text-neutral-500 w-12 flex-shrink-0">
                {formatHour(rdv.startHour)}
              </div>
              <div className="flex flex-col flex-1 min-w-0">
                <div className="text-h2 font-medium text-black truncate">{rdv.clientName}</div>
                {rdv.plate && <div className="text-h3 text-neutral-400">{rdv.plate}</div>}
              </div>
              <span
                className="text-h4 font-medium rounded-full px-2 py-0.5 flex-shrink-0"
                style={{ background: colors.bg, color: colors.text }}
              >
                {catLabels[rdv.category] || rdv.category}
              </span>
            </div>
          );
        })}
      </div>
      {appointments.length > 8 && (
        <div className="text-h3 text-neutral-400 text-center">+{appointments.length - 8} autres</div>
      )}
    </div>
  );
}
